// Delspår 3e Fas 2b — public activity-invitation accept page.
// Mirrors JoinTeam.tsx (public token route, RPC accept, Swedish error mapping, login-gate)
// but reads the invitation via the SECURITY DEFINER get_activity_invitation_preview RPC
// (NOT a direct activity_invitations SELECT — RLS keeps that table closed; the preview RPC
// returns only safe curated fields for the matching token, no enumeration / no email leak).
//
// Route: /invite/:token (public, registered in App.tsx public section). Handles both
// anon (show login) and authenticated (accept/decline) states.

import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  computeActivityJoinAmountMinorById,
  computeActivityJoinBreakdownMinorById,
} from "@/lib/computeActivityJoinAmountMinor";
import { chargePendingPayment } from "@/lib/chargePendingPayment";
import { formatPrice } from "@/utils/formatPrice";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CalendarCheck, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

type InvitePreview = {
  activity_id: string;
  title: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  org_name: string | null;
  status: string;
  is_expired: boolean;
};

const hhmm = (t: string | null) => (t ? t.slice(0, 5) : "");

const ActivityInvite = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(["admin", "activities"]);

  const { data: invite, isLoading } = useQuery<InvitePreview | null>({
    queryKey: ["activity-invite", token],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("get_activity_invitation_preview", {
        p_token: token,
      });
      if (error) throw error;
      return (data as InvitePreview | null) ?? null;
    },
    enabled: !!token,
  });

  // M3: show the CANONICAL charge before accept — an invitee must never accept a payment
  // obligation without seeing the amount (Fas 0: display-by-omission gap). Same source the
  // acceptMutation registers (computeActivityJoinAmountMinorById), so shown == registered.
  // Zero (free/external/court-less) -> no amount line.
  // L56 input gate: the accept button stays disabled until this breakdown has RESOLVED —
  // accepting before the price is known would register/charge an amount the user never saw
  // (and a failed query must never silently degrade to a different amount). Fail-closed.
  const { data: acceptBreakdown, isSuccess: breakdownReady } = useQuery({
    queryKey: ["invite-accept-breakdown", invite?.activity_id, user?.id],
    enabled: !!invite?.activity_id && !!user?.id && invite?.status === "pending" && !invite?.is_expired,
    queryFn: () => computeActivityJoinBreakdownMinorById(invite!.activity_id, user!.id),
  });

  const mapError = (msg: string): string => {
    if (/invitation_not_found/.test(msg)) return t("activityInvite.errors.notFound", { defaultValue: "Inbjudan hittades inte" });
    if (/invitation_not_pending/.test(msg)) return t("activityInvite.errors.notPending", { defaultValue: "Inbjudan har redan använts eller återkallats" });
    if (/invitation_expired/.test(msg)) return t("activityInvite.errors.expired", { defaultValue: "Inbjudan har gått ut" });
    if (/not_a_member_of_org/.test(msg)) return t("activityInvite.errors.notMember", { defaultValue: "Du måste vara medlem i klubben för att gå med" });
    // FYND #1: the 2d-i full-gate makes accept_activity_invitation raise 'activity_full' on a full activity.
    if (/activity_full/.test(msg)) return t("activities:errors.activityFull", { defaultValue: "Aktiviteten är full" });
    return t("activityInvite.errors.generic", { defaultValue: "Något gick fel" });
  };

  // 2d-iii B2 — Modell A (betala-vid-accept): accept registers the pending payment AND settles it
  // in the same flow. v2 exposes payment_id (the old RPC discarded it); the charge triple mirrors
  // the join path (chargePendingPayment, Lesson 38). Operator decision (b): a failed charge keeps
  // the seat + the pending row — retry lives on the detail page's "Slutför betalning" CTA.
  type AcceptOutcome =
    | { outcome: "free" | "paid" | "skipped" | "redirect"; activityId: string }
    | { outcome: "failed"; activityId: string };

  const acceptMutation = useMutation({
    mutationFn: async (): Promise<AcceptOutcome> => {
      const amountMinor =
        invite?.activity_id && user?.id
          ? await computeActivityJoinAmountMinorById(invite.activity_id, user.id)
          : null;
      const { data, error } = await (supabase.rpc as any)("accept_activity_invitation_v2", {
        p_token: token,
        p_amount_minor: amountMinor,
      });
      if (error) throw new Error(mapError(error.message || ""));
      const res = data as { activity_id: string; payment_id: string | null };
      const activityId = res.activity_id;

      // Free/external/court-less accept (payment_id null) → pure accept, no payment UI (L60).
      if (!res.payment_id || !amountMinor || amountMinor <= 0) {
        return { outcome: "free", activityId };
      }

      // Resolve the activity's org (saved cards are PER-ORG; the invite page has no org context)
      // + the player's MIT card in that org. Same default-first pick as the join path.
      const { data: act } = await supabase
        .from("activities")
        .select("organization_id, activity_title")
        .eq("id", activityId)
        .single();
      const orgId = (act as { organization_id?: string } | null)?.organization_id;
      if (!orgId) return { outcome: "skipped", activityId };
      const { data: cards } = await supabase
        .from("player_payment_methods" as any)
        .select("id, is_mit_enabled, is_default")
        .eq("user_id", user!.id)
        .eq("organization_id", orgId);
      const mitCards = ((cards ?? []) as { id: string; is_mit_enabled: boolean; is_default: boolean }[])
        .filter((c) => c.is_mit_enabled);
      const mitCard = mitCards.find((c) => c.is_default) ?? mitCards[0];
      const description = `Aktivitet: ${(act as { activity_title?: string | null }).activity_title ?? activityId}`;

      if (mitCard) {
        // Saved card → off-session charge (paid/skipped/failed).
        const charge = await chargePendingPayment({
          userId: user!.id,
          orgId,
          amountMinor,
          paymentId: res.payment_id,
          savedCardId: mitCard.id,
          description,
          relatedEntityType: "activity",
          relatedEntityId: activityId,
        });
        if (charge.status === "paid") return { outcome: "paid", activityId };
        if (charge.status === "skipped") return { outcome: "skipped", activityId };
        return { outcome: "failed", activityId };
      }

      // No card → Stripe Checkout redirect against the activity's linked block booking.
      // An unarmed org never reaches Stripe: B1's guard returns {skipped:'charge_disabled'}
      // which the helper maps to 'skipped' — graceful soft-info, NO redirect attempt.
      const { data: bks } = await supabase
        .from("bookings")
        .select("id")
        .eq("activity_id", activityId)
        .limit(1);
      const bookingId = (bks ?? [])[0]?.id;
      if (!bookingId) return { outcome: "skipped", activityId };
      const charge = await chargePendingPayment({
        userId: user!.id,
        orgId,
        amountMinor,
        paymentId: res.payment_id,
        savedCardId: null,
        description,
        relatedEntityType: "activity",
        relatedEntityId: activityId,
        bookingId,
        successUrl: `${window.location.origin}/activities/${activityId}?payment=success`,
        cancelUrl: `${window.location.origin}/activities/${activityId}`,
      });
      if (charge.status === "redirect") {
        window.location.href = charge.url;
        return { outcome: "redirect", activityId };
      }
      if (charge.status === "skipped") return { outcome: "skipped", activityId };
      return { outcome: "failed", activityId };
    },
    onSuccess: (res) => {
      // Navigation fix: the logged-in detail page is /activities/:id (ProtectedRoute) —
      // /aktiviteter/:id is the ANONYMOUS detail page (the stranding bug).
      if (res.outcome === "redirect") return; // browser is navigating to Stripe
      if (res.outcome === "failed") {
        // Decision (b): seat + pending kept; point at the way out (rider 2).
        toast.error(t("activityInvite.paymentDeclinedRetry", {
          defaultValue: "Betalningen nekades — försök igen via Slutför betalning, eller uppdatera ditt kort under Profil",
        }));
        navigate(`/activities/${res.activityId}`);
        return;
      }
      if (res.outcome === "paid") {
        toast.success(t("activityInvite.acceptedPaid", { defaultValue: "Du är med — betalningen är genomförd!" }));
      } else if (res.outcome === "skipped") {
        toast.success(t("activityInvite.acceptedPaymentLater", { defaultValue: "Du är med i aktiviteten! Betalningen hanteras senare." }));
      } else {
        toast.success(t("activityInvite.accepted", { defaultValue: "Du är nu med i aktiviteten!" }));
      }
      navigate(`/activities/${res.activityId}`);
    },
    onError: (err: Error) => toast.error(err.message || t("activityInvite.errors.generic", { defaultValue: "Något gick fel" })),
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase.rpc as any)("reject_activity_invitation", { p_token: token });
      if (error) throw new Error(mapError(error.message || ""));
    },
    onSuccess: () => {
      toast.success(t("activityInvite.declined", { defaultValue: "Du har avböjt inbjudan" }));
      navigate("/aktiviteter");
    },
    onError: (err: Error) => toast.error(err.message || t("activityInvite.errors.generic", { defaultValue: "Något gick fel" })),
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">{t("activityInvite.loading", { defaultValue: "Laddar inbjudan..." })}</p>
        </div>
      );
    }

    if (!invite) {
      return (
        <div className="flex flex-col items-center gap-2" data-testid="invite-not-found">
          <XCircle className="h-8 w-8 text-destructive" />
          <p className="text-muted-foreground">{t("activityInvite.notFound", { defaultValue: "Inbjudan hittades inte eller har redan använts" })}</p>
          <Link to="/aktiviteter"><Button variant="outline" className="mt-2">{t("activityInvite.goToActivities", { defaultValue: "Gå till aktiviteter" })}</Button></Link>
        </div>
      );
    }

    if (invite.is_expired || invite.status === "expired") {
      return (
        <div className="flex flex-col items-center gap-2" data-testid="invite-expired">
          <Clock className="h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">{t("activityInvite.expired", { defaultValue: "Inbjudan har gått ut" })}</p>
          <Link to="/aktiviteter"><Button variant="outline" className="mt-2">{t("activityInvite.goToActivities", { defaultValue: "Gå till aktiviteter" })}</Button></Link>
        </div>
      );
    }

    if (invite.status !== "pending") {
      return (
        <div className="flex flex-col items-center gap-2" data-testid="invite-used">
          <CheckCircle2 className="h-8 w-8 text-green-500" />
          <p className="text-muted-foreground">{t("activityInvite.alreadyHandled", { defaultValue: "Inbjudan har redan besvarats" })}</p>
          <Link to="/aktiviteter"><Button variant="outline" className="mt-2">{t("activityInvite.goToActivities", { defaultValue: "Gå till aktiviteter" })}</Button></Link>
        </div>
      );
    }

    if (!user) {
      return (
        <div className="flex flex-col items-center gap-3" data-testid="invite-login">
          <p className="text-muted-foreground">{t("activityInvite.loginPrompt", { defaultValue: "Logga in eller skapa konto för att svara på inbjudan" })}</p>
          <Link to={`/auth?redirect=/invite/${token}`}><Button className="w-full">{t("activityInvite.login", { defaultValue: "Logga in" })}</Button></Link>
        </div>
      );
    }

    const title = invite.title || t("activityInvite.fallbackTitle", { defaultValue: "en aktivitet" });
    const timeStr = invite.start_time ? `${hhmm(invite.start_time)}–${hhmm(invite.end_time)}` : "";
    const busy = acceptMutation.isPending || rejectMutation.isPending;

    return (
      <div className="flex flex-col items-center gap-3" data-testid="invite-pending">
        <p className="text-lg">{t("activityInvite.invitedTo", { defaultValue: "Du är inbjuden till {{title}}", title })}</p>
        {(invite.date || timeStr) && (
          <p className="text-muted-foreground">{[invite.date, timeStr].filter(Boolean).join(" · ")}</p>
        )}
        {invite.org_name && <p className="text-sm text-muted-foreground">{invite.org_name}</p>}
        {acceptBreakdown && acceptBreakdown.totalMinor > 0 && (
          <p className="text-sm font-medium" data-testid="invite-charge-info">
            {t("activityInvite.acceptCharge", {
              defaultValue: "Du accepterar att betala {{amount}}",
              amount: formatPrice({ valueMinor: acceptBreakdown.totalMinor, currency: "SEK" }),
            })}
          </p>
        )}
        <Button className="w-full mt-2" data-testid="invite-accept" onClick={() => acceptMutation.mutate()} disabled={busy || !breakdownReady}>
          {acceptMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("activityInvite.accepting", { defaultValue: "Accepterar..." })}</>
          ) : acceptBreakdown && acceptBreakdown.totalMinor > 0 ? (
            t("activityInvite.acceptAndPay", {
              defaultValue: "Acceptera och betala {{amount}}",
              amount: formatPrice({ valueMinor: acceptBreakdown.totalMinor, currency: "SEK" }),
            })
          ) : (
            t("activityInvite.accept", { defaultValue: "Acceptera och gå med" })
          )}
        </Button>
        <Button variant="ghost" className="w-full" data-testid="invite-decline" onClick={() => rejectMutation.mutate()} disabled={busy}>
          {rejectMutation.isPending ? t("activityInvite.declining", { defaultValue: "Avböjer..." }) : t("activityInvite.decline", { defaultValue: "Avböj" })}
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CalendarCheck className="h-10 w-10 mx-auto text-primary mb-2" />
          <CardTitle>{t("activityInvite.cardTitle", { defaultValue: "Inbjudan till aktivitet" })}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">{renderContent()}</CardContent>
      </Card>
    </div>
  );
};

export default ActivityInvite;
